import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  AUTO: 'auto'
};

const STORAGE_KEY = 'belotto-pokeclass-theme';

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    // Load from localStorage on initial render
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored || THEMES.AUTO;
  });

  const [resolvedTheme, setResolvedTheme] = useState(THEMES.LIGHT);

  // Detect system preference
  const getSystemTheme = useCallback(() => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return THEMES.DARK;
    }
    return THEMES.LIGHT;
  }, []);

  // Resolve the actual theme to apply (handles AUTO mode)
  const resolveTheme = useCallback((themeValue) => {
    if (themeValue === THEMES.AUTO) {
      return getSystemTheme();
    }
    return themeValue;
  }, [getSystemTheme]);

  // Apply theme to document
  const applyTheme = useCallback((themeValue) => {
    const resolved = resolveTheme(themeValue);
    setResolvedTheme(resolved);
    
    // Remove both classes first
    document.documentElement.classList.remove('theme-light', 'theme-dark', 'dark');
    // Add the correct class
    document.documentElement.classList.add(`theme-${resolved}`);
    // Also add Tailwind's 'dark' class so all `dark:` utility variants work
    if (resolved === THEMES.DARK) {
      document.documentElement.classList.add('dark');
    }
    
    // Also set data attribute for CSS selectors
    document.documentElement.setAttribute('data-theme', resolved);
  }, [resolveTheme]);

  // Set theme and persist
  const setTheme = useCallback((newTheme) => {
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);
    applyTheme(newTheme);
  }, [applyTheme]);

  // Initial theme application
  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  // Listen for system theme changes (for AUTO mode)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = () => {
      if (theme === THEMES.AUTO) {
        applyTheme(THEMES.AUTO);
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [theme, applyTheme]);

  const value = {
    theme,           // Current setting (light/dark/auto)
    resolvedTheme,   // Actual theme being applied (light/dark)
    setTheme,
    isDark: resolvedTheme === THEMES.DARK
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
